import React from "react";
import { UserRound, FileText, Building2, UserCheck } from "lucide-react";
import { resolveContractorName, isContractorInternal } from "./PermitSignatureCard";
import { getFormattedPtwNo } from "./permitHelpers";
import { getInternalOrganizationName } from "../../shared/safetyHeaderUtils";

/**
 * Resolve canonical Wing for Permit card display.
 * Falls back to form_data or "Not Available".
 */
export const getPermitWing = (item) => {
  if (!item) return "Not Available";
  const direct = item?.wing;
  if (direct && String(direct).trim() && String(direct).trim() !== "—" && String(direct).trim() !== "-") {
    return String(direct).trim();
  }
  const formData = item?.form_data;
  if (formData && typeof formData === "object") {
    if (formData.location && typeof formData.location === "object") {
      const w = formData.location.wing || formData.location.Wing || formData.location.tower || formData.location.Tower;
      if (w && String(w).trim()) return String(w).trim();
    }
    for (const key of ["wing", "location_wing", "project_wing", "wing_name", "tower", "tower_name", "building"]) {
      if (formData[key] && String(formData[key]).trim()) {
        return String(formData[key]).trim();
      }
    }
  }
  return "Not Available";
};

/**
 * Resolve canonical Currently With for Permit card display.
 * Returns authoritative workflow owner or fallback "Not Available".
 */
export const getPermitCurrentlyWith = (item) => {
  if (!item) return "Not Available";
  const direct = item?.currently_with;
  const isFinalRejected = Boolean(
    item?.is_final_rejected ||
    (item?.issue_rejection_count && Number(item.issue_rejection_count) >= 3)
  );
  const isFinalCancelled = Boolean(
    item?.is_final_cancelled ||
    (item?.cancel_count && Number(item.cancel_count) >= 3)
  );

  const isClosureFlow = Boolean(
    item?.is_closure_resubmit ||
    item?.is_closure_rejection ||
    (item?.closure_rejection_count && Number(item.closure_rejection_count) > 0) ||
    String(item?.current_status || "").trim().toLowerCase() === "closure_in_progress" ||
    String(item?.current_status || "").trim().toLowerCase() === "closure" ||
    String(item?.held_from_status || "").trim().toLowerCase() === "closure_in_progress" ||
    String(item?.last_status_from || "").trim().toLowerCase() === "closure_in_progress" ||
    String(item?.current_stage || "").toLowerCase().includes("closure") ||
    String(item?.current_stage_label || "").toLowerCase().includes("closure") ||
    String(item?.current_assigned_group_name || "").toLowerCase().includes("closure")
  );

  const getMakerActualName = (target) => {
    const raw =
      target?.applicant_name ||
      target?.created_by_name ||
      target?.form_data?.applicant_name ||
      target?.form_data?.permit_applicant ||
      target?.form_data?.name_of_applicant ||
      target?.form_data?.maker_name ||
      target?.created_by?.full_name ||
      target?.created_by?.name ||
      target?.created_by?.username;
    if (raw && String(raw).trim()) {
      const str = String(raw).trim();
      const lower = str.toLowerCase();
      if (
        lower !== "permit holder" &&
        lower !== "applicant / maker" &&
        lower !== "not available" &&
        lower !== "none" &&
        lower !== "null"
      ) {
        return str;
      }
    }
    return "";
  };

  if (
    direct &&
    String(direct).trim() &&
    String(direct).trim() !== "—" &&
    String(direct).trim() !== "-" &&
    String(direct).trim().toLowerCase() !== "not available"
  ) {
    const directLower = String(direct).trim().toLowerCase();

    if (directLower === "permit holder") {
      if (isClosureFlow) {
        const maker = getMakerActualName(item);
        return maker || "Not Available";
      }
      return "Permit Holder";
    }

    if (directLower === "rejected") {
      if (isClosureFlow) {
        const maker = getMakerActualName(item);
        return maker || "Not Available";
      }
      if (isFinalRejected) return "Rejected";
      const holder = getMakerActualName(item);
      return holder ? `Applicant / Maker (${holder})` : "Applicant / Maker";
    }

    if (directLower === "cancelled") {
      if (isFinalCancelled) return "Cancelled";
      const holder = getMakerActualName(item);
      return holder ? `Applicant / Maker (${holder})` : "Applicant / Maker";
    }

    if (directLower === "applicant / maker") {
      if (isClosureFlow) {
        const maker = getMakerActualName(item);
        return maker || "Not Available";
      }
      const holder = getMakerActualName(item);
      return holder ? `Applicant / Maker (${holder})` : "Applicant / Maker";
    }

    if (
      directLower.startsWith("applicant / maker (") ||
      directLower.startsWith("permit holder (")
    ) {
      if (isClosureFlow) {
        const maker = getMakerActualName(item);
        if (maker) return maker;
        const match = String(direct).match(/\((.+)\)/);
        if (match && match[1] && match[1].trim()) return match[1].trim();
        return "Not Available";
      }
      return String(direct).trim();
    }

    if (directLower === "internal") {
      return getPermitInternalTeamName(item);
    }

    return String(direct).trim();
  }

  if (item?.current_assigned_group_name && String(item.current_assigned_group_name).trim()) {
    const group = String(item.current_assigned_group_name).trim();
    const gLower = group.toLowerCase();
    if (gLower === "internal") {
      return getPermitInternalTeamName(item);
    }
    if (gLower === "permit holder" || gLower === "maker" || gLower === "applicant") {
      if (isClosureFlow) {
        const maker = getMakerActualName(item);
        return maker || "Not Available";
      }
    }
    return group;
  }

  const status = String(item?.current_status || "").trim().toLowerCase();
  if (isClosureFlow) {
    const maker = getMakerActualName(item);
    return maker || "Not Available";
  }

  if (status === "issued") {
    const holder = getMakerActualName(item);
    return holder ? `Permit Holder (${holder})` : "Permit Holder";
  }
  if (status === "draft") {
    return "Applicant / Maker";
  }
  if (status === "rejected") {
    if (isFinalRejected) return "Rejected";
    const holder = getMakerActualName(item);
    return holder ? `Applicant / Maker (${holder})` : "Applicant / Maker";
  }
  if (status === "cancelled") {
    if (isFinalCancelled) return "Cancelled";
    const holder = getMakerActualName(item);
    return holder ? `Applicant / Maker (${holder})` : "Applicant / Maker";
  }
  if (status === "closed" || status === "completed") {
    return "Completed";
  }
  return "Not Available";
};

/**
 * Safely resolves project ID for a permit item or context.
 */
export const resolvePermitProjectId = (item, explicitProjectId = null) => {
  if (explicitProjectId != null && String(explicitProjectId).trim() !== "") {
    return String(explicitProjectId).trim();
  }
  const candidate =
    item?.project_id ??
    (typeof item?.project === "object" ? item?.project?.id : item?.project) ??
    item?.form_data?.project_id ??
    (typeof item?.form_data?.project === "object"
      ? item?.form_data?.project?.id
      : item?.form_data?.project) ??
    null;

  if (candidate != null && String(candidate).trim() !== "") {
    return String(candidate).trim();
  }

  try {
    const active =
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      localStorage.getItem("project_id");
    if (active != null && String(active).trim() !== "") {
      return String(active).trim();
    }
  } catch (e) {
    // Ignore localStorage errors in SSR/test environments
  }

  return "";
};

/**
 * Checks whether the permit item or active context belongs to Project 165.
 */
export const isPermitProject165 = (item, explicitProjectId = null) => {
  return resolvePermitProjectId(item, explicitProjectId) === "165";
};

/**
 * Returns the project-scoped display label for the internal team/contractor.
 * For project ID 165 only, returns "Arkade Developers Limited".
 * For all other projects, returns "Internal".
 */
export const getPermitInternalTeamName = (item, explicitProjectId = null) => {
  return isPermitProject165(item, explicitProjectId)
    ? "Arkade Developers Limited"
    : "Internal";
};

/**
 * Checks whether the contractor associated with the permit is Internal.
 */
export const isPermitContractorInternal = (item) => {
  const name = resolveContractorName(item);
  return isContractorInternal(name, item);
};

/**
 * Resolve canonical contractor name for Permit card display.
 * Returns the resolved business contractor name or "Internal" (or "Arkade Developers Limited" for project 165).
 */
export const getPermitContractor = (item, explicitProjectId = null) => {
  const isInternal = isPermitContractorInternal(item);
  if (isInternal) {
    return getPermitInternalTeamName(item, explicitProjectId);
  }
  const name = resolveContractorName(item);
  if (name && String(name).trim()) {
    const trimmed = String(name).trim();
    if (trimmed.toLowerCase() === "internal") {
      return getPermitInternalTeamName(item, explicitProjectId);
    }
    return trimmed;
  }
  return "External";
};

/**
 * Resolve canonical PTW number for Permit card display.
 * Returns the formatted/reserved permit number or fallback "—".
 */
export const getPermitPtwNumber = (item) => {
  const direct =
    getFormattedPtwNo(item) ||
    (item?.ptw_display_no ? String(item.ptw_display_no).trim() : "") ||
    (item?.ptw_no ? String(item.ptw_no).trim() : "");

  return direct && direct !== "null" && direct !== "undefined" ? direct : "—";
};

/**
 * Clean card title by removing any prepended PTW number or ID,
 * returning the clean work/template name (e.g. "HEIGHT WORK").
 */
export const getPermitCardTitle = (item) => {
  if (!item) return "Permit";

  // If template_name or name is explicitly provided
  let title = String(
    item.template_name ||
    item.template?.name ||
    item.name ||
    ""
  ).trim();

  // If title was composed with `${ptwNo} - ${name}`, strip the prefix
  if (title.includes(" - ")) {
    const parts = title.split(" - ");
    if (parts.length >= 2 && /^ADL-|^PTW-|^[A-Z0-9]+-[A-Z0-9]+-/i.test(parts[0].trim())) {
      title = parts.slice(1).join(" - ").trim();
    }
  }

  if (title) return title;
  return item.id ? `Permit #${item.id}` : "Permit";
};

/**
 * Reusable metadata chip styled consistently with Safety Checklist cards.
 */
export const MetaChip = ({
  icon,
  label,
  value,
  tone = "slate",
  title,
  noTruncate = false,
}) => {
  const toneClasses = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-700";

  return (
    <span
      title={title || (typeof value === "string" && value !== "—" ? value : undefined)}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium leading-tight shrink-0 ${toneClasses}`}
    >
      {icon}
      {label && (
        <span className="text-muted-foreground/80 whitespace-nowrap shrink-0">{label}</span>
      )}
      <span
        className={`font-semibold ${
          noTruncate
            ? "whitespace-nowrap shrink-0"
            : "truncate max-w-[200px] sm:max-w-[320px]"
        }`}
      >
        {typeof value === "object" && value !== null
          ? value.label || value.value || JSON.stringify(value)
          : value}
      </span>
    </span>
  );
};

/**
 * Row 3 metadata badges for Permit dashboard cards.
 * Displays Contractor and canonical PTW No. in compact pills.
 */
export default function PermitListMeta({ item, projectId }) {
  const contractor = getPermitContractor(item, projectId);
  const ptwNo = getPermitPtwNumber(item);
  const wing = getPermitWing(item);
  const currentlyWith = getPermitCurrentlyWith(item);
  const isInternal = isPermitContractorInternal(item) || contractor.toLowerCase() === "internal";
  const internalName = getPermitInternalTeamName(item, projectId);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
      <MetaChip
        icon={<UserRound className="h-3 w-3 shrink-0 text-blue-600" />}
        value={contractor}
        tone="blue"
        title={isInternal ? internalName : `Contractor: ${contractor}`}
      />

      <MetaChip
        icon={<Building2 className="h-3 w-3 shrink-0 text-slate-600" />}
        label="Wing:"
        value={wing}
        tone="slate"
        title={`Wing: ${wing}`}
      />

      <MetaChip
        icon={<FileText className="h-3 w-3 shrink-0 text-slate-600" />}
        label="PTW No.:"
        value={ptwNo}
        tone="slate"
        noTruncate={true}
        title={ptwNo !== "—" ? `PTW No.: ${ptwNo}` : undefined}
      />

      {currentlyWith && (
        <MetaChip
          icon={<UserCheck className="h-3 w-3 shrink-0 text-slate-700" />}
          label="Currently With:"
          value={currentlyWith}
          tone="slate"
          title={`Currently With: ${currentlyWith}`}
        />
      )}
    </div>
  );
}
